import React, { useState, useRef } from 'react';
import './MainPage.css';
import {
  Box,
  Flex,
  Heading,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Stack,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

export default function MainPage() {
  return (
    <Box as="main" id="main" p={6}>
      <NavBar />
      <Hero />
      <Box as="section" id="join" className="maitri-join" aria-label="Join">
        <Join />
      </Box>
      <Services />
      
      <Benefits />

      <Testimonials />

      <About />

      

      <Footer />
    </Box>
  );
}

function Testimonials() {
  const testimonials = [
    {
      name: 'Suman Patel',
      role: 'Member since 2015',
      quote: 'Maitri helped me secure a home loan with clear terms and friendly support. Highly recommended for families looking for trustworthy financial help.',
    },
    {
      name: 'Rajeshwari Singh',
      role: 'Small Business Owner',
      quote: 'Their recurring deposit plan made saving easy and predictable. The staff always explain options patiently.',
    },
    {
      name: 'Arjun Mehta',
      role: 'Teacher',
      quote: 'Transparent practices and prompt service — I feel valued as a member.',
    },
  ];

  return (
    <Box as="section" id="testimonials" className="maitri-testimonials" aria-label="Testimonials">
      <Box className="testimonials-inner">
        <Heading as="h2" className="testimonials-title">Testimonials</Heading>
        <Text className="testimonials-desc">Member reviews — trust-building quotes from our community</Text>

        <Flex className="testimonials-grid" gap={6} justify="center" wrap="wrap">
          {testimonials.map((t, i) => (
            <Box className="testimonial-card" key={i}>
              <Text className="testimonial-quote">“{t.quote}”</Text>
              <Text className="testimonial-author">{t.name}</Text>
              <Text className="testimonial-role">{t.role}</Text>
            </Box>
          ))}
        </Flex>
      </Box>
    </Box>
  );
}

function JoinForm() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', occupation: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  }

  function validate(f) {
    const err = {};
    if (!f.name || !f.name.trim()) err.name = 'Name is required';
    if (!f.phone || !f.phone.trim()) err.phone = 'Phone number is required';
    if (!f.email || !f.email.trim()) err.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(f.email)) err.email = 'Enter a valid email';
    if (!f.address || !f.address.trim()) err.address = 'Address is required';
    return err;
  }

  function handleSubmit(e) {
    e.preventDefault();
    const err = validate(form);
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }

    setSubmitted(true);
    setForm({ name: '', phone: '', email: '', address: '', occupation: '' });
    setErrors({});
    setTimeout(() => setSubmitted(false), 4000);
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack spacing={3} className="join-form">
        <FormControl isRequired isInvalid={!!errors.name}>
          <FormLabel>Name</FormLabel>
          <Input name="name" placeholder="Full name" value={form.name} onChange={handleChange} />
          {errors.name && <FormErrorMessage>{errors.name}</FormErrorMessage>}
        </FormControl>

        <FormControl isRequired isInvalid={!!errors.phone}>
          <FormLabel>Phone Number</FormLabel>
          <Input name="phone" type="tel" placeholder="e.g. +91 98765 43210" value={form.phone} onChange={handleChange} />
          {errors.phone && <FormErrorMessage>{errors.phone}</FormErrorMessage>}
        </FormControl>

        <FormControl isRequired isInvalid={!!errors.email}>
          <FormLabel>Email</FormLabel>
          <Input name="email" type="email" placeholder="you@example.com" value={form.email} onChange={handleChange} />
          {errors.email && <FormErrorMessage>{errors.email}</FormErrorMessage>}
        </FormControl>

        <FormControl isRequired isInvalid={!!errors.address}>
          <FormLabel>Address</FormLabel>
          <Textarea name="address" placeholder="Postal address" value={form.address} onChange={handleChange} rows={3} />
          {errors.address && <FormErrorMessage>{errors.address}</FormErrorMessage>}
        </FormControl>

        <FormControl>
          <FormLabel>Occupation (optional)</FormLabel>
          <Input name="occupation" placeholder="e.g. Teacher, Small Business Owner" value={form.occupation} onChange={handleChange} />
        </FormControl>

        <Button colorScheme="teal" type="submit">Submit Application</Button>

        {submitted && <Text color="green.600">Application submitted — we'll contact you shortly.</Text>}
      </Stack>
    </form>
  );
}

function Hero() {
  return (
    <Box as="section" className="maitri-hero" aria-label="Hero">
      <Box className="hero-inner">
        <Heading className="hero-headline">Building Financial Trust Together</Heading>
        <Text className="hero-subtext">Empowering our community through transparent, member-first financial services.</Text>

        <Stack direction={{ base: 'column', md: 'row' }} spacing={4} className="hero-buttons" justify="center">
          <Button colorScheme="teal" as="a" href="#join-heading">Become a Member</Button>
          <Button variant="outline" colorScheme="teal" as="a" href="#services">Explore Services</Button>
        </Stack>
      </Box>
    </Box>
  );
}

function About() {
  return (
    <Box as="section" className="maitri-about" aria-label="About">
      <Box className="about-inner">
        <Heading as="h2" className="about-title">About Our Society</Heading>
        <Text className="about-desc">Maitri Credit Co-operative Society is a member-driven financial institution focused on transparent, community-first services that help members grow and secure their financial future.</Text>

        <Flex className="about-highlights" gap={6} justify="center" wrap="wrap">
          <Box className="about-card">
            <Heading size="md">25+ Years</Heading>
            <Text>Years of service</Text>
          </Box>

          <Box className="about-card">
            <Heading size="md">12,000+</Heading>
            <Text>Active members</Text>
          </Box>

          <Box className="about-card">
            <Heading size="md">Trusted</Heading>
            <Text>High member satisfaction & trust</Text>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}

function Services() {
  return (
    <Box as="section" id="services" className="maitri-services" aria-label="Services">
      <Box className="services-inner">
        <Heading as="h2" className="services-title">Our Services</Heading>
        <Text className="services-desc">We offer a range of member-first financial products designed for growth and security.</Text>

        <Flex className="services-layout" gap={8} justify="center" wrap="wrap" align="flex-start">
          <Box className="services-list" flex="1">
            <Flex className="services-grid" gap={6} justify="flex-start" wrap="wrap">
              <Box className="service-card">
                <Text className="service-emoji">💰</Text>
                <Heading size="sm">Savings Accounts</Heading>
                <Text>Secure savings with flexible access and competitive rates.</Text>
              </Box>

              <Box className="service-card">
                <Text className="service-emoji">📈</Text>
                <Heading size="sm">Fixed Deposits</Heading>
                <Text>Higher returns for fixed-term investments.</Text>
              </Box>

              <Box className="service-card">
                <Text className="service-emoji">🏠</Text>
                <Heading size="sm">Loans</Heading>
                <Text>Personal, Home, and Business loans with fair terms.</Text>
              </Box>

              <Box className="service-card">
                <Text className="service-emoji">🔄</Text>
                <Heading size="sm">Recurring Deposits</Heading>
                <Text>Automated monthly saving plans for steady growth.</Text>
              </Box>
            </Flex>
          </Box>

         
        </Flex>
      </Box>
    </Box>
  );
}

function DemoCarousel() {
  const items = Array.from({ length: 5 });
  const containerRef = useRef(null);
  const [index, setIndex] = useState(0);
  const slideWidth = 520; // px (main slide width)
  const gap = 24; // px

  function prev() {
    const next = Math.max(0, index - 1);
    scrollToIndex(next);
  }

  function next() {
    const nextIndex = Math.min(items.length - 1, index + 1);
    scrollToIndex(nextIndex);
  }

  function scrollToIndex(i) {
    const el = containerRef.current;
    if (!el) return;
    const offset = Math.max(0, i * (slideWidth + gap) - (el.clientWidth - slideWidth) / 2);
    el.scrollTo({ left: offset, behavior: 'smooth' });
    setIndex(i);
  }

  function handleScroll() {
    const el = containerRef.current;
    if (!el) return;
    const centerOffset = el.scrollLeft + (el.clientWidth - slideWidth) / 2;
    const i = Math.round(centerOffset / (slideWidth + gap));
    setIndex(i);
  }

  return (
    <Box>
      <Flex align="center" gap={4} mb={4}>
        <IconButton size="sm" variant="ghost" aria-label="Prev" onClick={prev}>
          <ChevronLeftIcon />
        </IconButton>

        <Box flex="1" overflowX="auto" ref={containerRef} onScroll={handleScroll} css={{ scrollSnapType: 'x mandatory' }}>
          <Flex gap={`${gap}px`} pr="2" align="center">
            {items.map((_, i) => (
              <Box
                key={i}
                flex="0 0 auto"
                width={`${slideWidth}px`}
                height="360px"
                rounded="md"
                bg="gray.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="2.5rem"
                scrollSnapAlign="center"
                style={{
                  transform: `scale(${i === index ? 1 : 0.84})`,
                  transition: 'transform 300ms ease',
                  boxShadow: i === index ? '0 8px 24px rgba(0,0,0,0.12)' : 'none',
                }}
              >
                {i + 1}
              </Box>
            ))}
          </Flex>
        </Box>

        <IconButton size="sm" variant="ghost" aria-label="Next" onClick={next}>
          <ChevronRightIcon />
        </IconButton>
      </Flex>

      <Flex justify="center" gap={2}>
        {items.map((_, i) => (
          <Box
            key={i}
            as="button"
            onClick={() => scrollToIndex(i)}
            width={i === index ? '12px' : '8px'}
            height={i === index ? '12px' : '8px'}
            bg={i === index ? 'teal.500' : 'gray.300'}
            rounded="full"
            transition="all 150ms"
          />
        ))}
      </Flex>
    </Box>
  );
}

function Join() {
  return (
    <Box as="section" id="join" className="maitri-join" aria-label="Join">
      <Box className="join-inner">
        <Flex direction={{ base: 'column', md: 'row' }} mt={8} gap={8} align="flex-start" justify="center" wrap="wrap">
          <Box w={{ base: '100%', md: '60%' }} p={2}>
            <DemoCarousel />
          </Box>

          <Box w={{ base: '100%', md: '360px' }} p={6} rounded="md" boxShadow="sm" bg="white" zIndex={1}>
            <Heading as="h3" size="md" id="join-heading" mb={4}>
              Join Us
            </Heading>
            <JoinForm />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}

function Benefits() {
  return (
    <Box as="section" id="benefits" className="maitri-benefits" aria-label="Benefits">
      <Box className="benefits-inner">
        <Heading as="h2" className="benefits-title">Member Benefits</Heading>
        <Text className="benefits-desc">Our members enjoy practical advantages designed to make finance simpler and fairer.</Text>

        <Flex className="benefits-grid" gap={6} justify="center" wrap="wrap">
          <Box className="benefit-card">
            <Text className="benefit-emoji">💸</Text>
            <Heading size="sm">Low interest rates</Heading>
            <Text>Competitive, member-first lending rates.</Text>
          </Box>

          <Box className="benefit-card">
            <Text className="benefit-emoji">🔒</Text>
            <Heading size="sm">Secure investments</Heading>
            <Text>Safe deposit products and transparent practices.</Text>
          </Box>

          <Box className="benefit-card">
            <Text className="benefit-emoji">🤝</Text>
            <Heading size="sm">Community-driven</Heading>
            <Text>Decisions made by and for our members.</Text>
          </Box>

          <Box className="benefit-card">
            <Text className="benefit-emoji">✅</Text>
            <Heading size="sm">Easy loan approvals</Heading>
            <Text>Streamlined processes for timely support.</Text>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}

function NavBar() {
  return (
    <header className="maitri-nav" role="navigation">
      <div className="nav-left">
        <img
          src={`${process.env.PUBLIC_URL}/assets/maitri_logo.jpeg`}
          alt="Maitri logo"
          className="nav-logo"
        />
        <div className="nav-title">Maitri Credit Co-operative Society</div>
      </div>

      <nav className="nav-links" aria-label="Main menu">
        <a className="nav-link" href="#main">Home</a>
        <a className="nav-link" href="#intro-heading">About</a>

        <Menu>
          <MenuButton as={Button} className="nav-menu-button">
            Services ▾
          </MenuButton>
          <MenuList>
            <MenuItem as="a" href="#services">All Services</MenuItem>
            <MenuItem as="a" href="#services">Savings Accounts</MenuItem>
            <MenuItem as="a" href="#services">Fixed Deposits</MenuItem>
            <MenuItem as="a" href="#services">Loans</MenuItem>
            <MenuItem as="a" href="#services">Recurring Deposits</MenuItem>
          </MenuList>
        </Menu>

        <a className="nav-link" href="#join-heading">Membership</a>
        <a className="nav-link" href="#footer">Contact</a>
        <a className="nav-cta" href="#join-heading">Join Now</a>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <Box as="footer" id="footer" className="maitri-footer" mt={12} py={10}>
      <Box className="footer-inner">
        <Flex className="footer-grid" gap={8} justify="space-between" wrap="wrap">
          <Box className="footer-col">
            <Heading size="sm">Maitri Credit Co-operative Society</Heading>
            <Text mt={2}>Near Shalimar Hotel, Kudwa Naka , Gondia, Maharashtra</Text>
            <Text mt={1}>Phone: +91 77668 20409</Text>
            <Text mt={1}>Email: helpdesk@maitricredit.com</Text>
          </Box>

          <Box className="footer-col">
            <Heading size="sm">Quick Links</Heading>
            <Stack as="nav" spacing={2} className="footer-links" mt={2}>
              <a className="footer-link" href="#services">Services</a>
              <a className="footer-link" href="#benefits">Benefits</a>
              <a className="footer-link" href="#join-heading">Join</a>
              <a className="footer-link" href="#testimonials">Testimonials</a>
            </Stack>
          </Box>

          <Box className="footer-col">
            <Heading size="sm">Connect</Heading>
            <Flex gap={3} align="center" mt={2}>
              <a className="social-icon" href="https://facebook.com" aria-label="Facebook">🔵</a>
              <a className="social-icon" href="https://twitter.com" aria-label="Twitter">🐦</a>
              <a className="social-icon" href="https://wa.me/919876543210" aria-label="WhatsApp">💬</a>
            </Flex>

            <Text mt={4} className="footer-copyright">© 2026 Maitri Credit Co-operative Society. All rights reserved.</Text>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}
